// OCaml tests in a workspace: inline tests (ppx_inline_test: let%test, let%test_unit), expect
// tests (ppx_expect: let%expect_test) and Alcotest executables (Alcotest.run).

const PPX_TEST = /^\s*let%(test|test_unit|expect_test)\b(?:\s+"((?:[^"\\]|\\.)*)")?/;

export interface OcamlTest {
  kind: "test" | "test_unit" | "expect_test";
  /** As ppx_inline_test names it: the string after let%test, or "" for an unnamed test. */
  name: string;
  /** 1-based. */
  line: number;
}

/** The inline and expect tests in a file, in order. */
export function findPpxTests(text: string): OcamlTest[] {
  const tests: OcamlTest[] = [];
  text.split("\n").forEach((line, i) => {
    const match = PPX_TEST.exec(line);
    if (match) {
      tests.push({
        kind: match[1] as OcamlTest["kind"],
        name: (match[2] ?? "").replace(/\\(.)/g, "$1"),
        line: i + 1,
      });
    }
  });
  return tests;
}

export const hasPpxTests = (text: string) => findPpxTests(text).length > 0;

/** An Alcotest executable (runs its suites when executed). */
export const isAlcotestMain = (text: string) => /\bAlcotest\.run\b/.test(text);

/** The name results and code lenses use: ppx_inline_test's descr, or "line N" when unnamed. */
export const testName = (test: OcamlTest) => test.name || `line ${test.line}`;

/**
 * Expect tests whose output didn't match, from the file and ppx_expect's corrected version of it:
 * each changed line belongs to the expect test that starts at or before it.
 */
export function failedExpectTests(
  original: string,
  corrected: string,
  tests: OcamlTest[],
): Map<number, { line: number; expected: string; actual: string }> {
  const failed = new Map<number, { line: number; expected: string; actual: string }>();
  const expectTests = tests.filter((t) => t.kind === "expect_test");
  if (expectTests.length === 0 || original === corrected) return failed;
  for (const hunk of diffHunks(original.split("\n"), corrected.split("\n"))) {
    const owner = [...expectTests].reverse().find((t) => t.line <= hunk.line);
    if (!owner) continue;
    const previous = failed.get(owner.line);
    failed.set(owner.line, {
      line: previous?.line ?? hunk.line,
      expected: previous
        ? `${previous.expected}\n${hunk.removed.join("\n")}`
        : hunk.removed.join("\n"),
      actual: previous ? `${previous.actual}\n${hunk.added.join("\n")}` : hunk.added.join("\n"),
    });
  }
  return failed;
}

interface Hunk {
  /** 1-based line in the original where the change starts. */
  line: number;
  removed: string[];
  added: string[];
}

/** Line-level diff (LCS); test files are small. */
export function diffHunks(a: string[], b: string[]): Hunk[] {
  const n = a.length;
  const m = b.length;
  // lcs(i, j): the longest common subsequence of a[i..] and b[j..].
  const table = new Uint32Array((n + 1) * (m + 1));
  const lcs = (i: number, j: number) => table[i * (m + 1) + j] ?? 0;
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      table[i * (m + 1) + j] =
        a[i] === b[j] ? lcs(i + 1, j + 1) + 1 : Math.max(lcs(i + 1, j), lcs(i, j + 1));
    }
  }
  const hunks: Hunk[] = [];
  let current: Hunk | undefined;
  let i = 0;
  let j = 0;
  while (i < n || j < m) {
    if (i < n && j < m && a[i] === b[j]) {
      if (current) hunks.push(current);
      current = undefined;
      i++;
      j++;
    } else if (j < m && (i === n || lcs(i, j + 1) >= lcs(i + 1, j))) {
      current ??= { line: i + 1, removed: [], added: [] };
      current.added.push(b[j++] ?? "");
    } else {
      current ??= { line: i + 1, removed: [], added: [] };
      current.removed.push(a[i++] ?? "");
    }
  }
  if (current) hunks.push(current);
  return hunks;
}

const ALCOTEST_RESULT = /^\s*\[(OK|FAIL|SKIP)\]\s+(.+?)\s+(\d+)\s+(.*?)\.?\s*$/;

/** One line of Alcotest's report, e.g. "  [OK]          math          0   adds." */
export function parseAlcotestLine(
  line: string,
): { status: "pass" | "fail" | "skip"; suite: string; name: string } | undefined {
  // biome-ignore lint/suspicious/noControlCharactersInRegex: ANSI color codes
  const match = ALCOTEST_RESULT.exec(line.replace(/\x1b\[[0-9;]*m/g, ""));
  if (!match) return undefined;
  const [, kind, suite = "", , name = ""] = match;
  const status = kind === "OK" ? "pass" : kind === "FAIL" ? "fail" : "skip";
  return { status, suite: suite.trim(), name: name.trim() };
}
